import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from '@remix-run/react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import ContactPageMobile from '../component/contact-tab/ContactPage.mobile';
import ContactPageDesktop from '../component/contact-tab/ContactPage.desktop';
import type { APHandle } from '~/modules/root';
import { useProfessionalInitialRouteData } from '../hook/use-professional-initial-route-data';
import {
    getInitialContactID,
    getLinkedProfileLocations,
} from '../util/contact-page-helper';
import type { LocationStateType } from '../type/contact-page-types';
import { useLoaderData } from '@remix-run/react';
import type { ContactLoaderData } from '../type/contact-page-types';
import { useTracker } from '@archipro-website/tracker';

const ContactPage = () => {
    const { desktop } = useAppDisplayModeContext();
    const tracker = useTracker();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const professional = useProfessionalInitialRouteData();
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];

    // Set sessionStorage synchronously before child components mount
    // This ensures ClickToReveal components read the correct revealed state on mount
    useMemo(() => {
        const shouldAutoRevealAddress =
            new URLSearchParams(location.search).get('expandAddress') ===
            'true';

        if (shouldAutoRevealAddress && professional) {
            const originalLocations = getLinkedProfileLocations(
                professional,
                linkedProfiles ?? {
                    linkedProfilesCount: 0,
                    linkedProfilesList: [],
                    linkedProfilesProductSharing: [],
                },
            );

            if (originalLocations.length > 0) {
                const locationIDToReveal = getInitialContactID(
                    (location.state as LocationStateType) ||
                        ({} as LocationStateType),
                    searchParams,
                );
                const locationToReveal = locationIDToReveal
                    ? originalLocations.find(
                          (loc) => loc.ID === locationIDToReveal,
                      ) || originalLocations[0]
                    : originalLocations[0];

                if (locationToReveal) {
                    const sessionKey = `reveal-Location-professional-${professional.ID}`;
                    const revealedSubKeys = JSON.parse(
                        sessionStorage.getItem(sessionKey) || '[]',
                    );

                    if (
                        !revealedSubKeys.includes(
                            locationToReveal.ID.toString(),
                        )
                    ) {
                        // Set sessionStorage synchronously before components mount
                        const updatedRevealedSubKeys = [
                            ...revealedSubKeys,
                            locationToReveal.ID.toString(),
                        ];
                        sessionStorage.setItem(
                            sessionKey,
                            JSON.stringify(updatedRevealedSubKeys),
                        );

                        // Log the event after setting sessionStorage
                        tracker.log('LocationReveal', {
                            url: new URL(window.location.href),
                        });
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, professional?.ID]);

    // For some reason, Remix scroll restoration brings page back to the top
    // on suppliers detail pages. This is here to ensure the page scrolls to the
    // contact section anchor after it has loaded.
    useEffect(() => {
        if (location.hash && professional) {
            // Remove the # from the hash to get the element ID
            const elementId = location.hash.substring(1);

            // Use requestAnimationFrame to ensure we run after all other scroll operations
            // Then add a delay to ensure Remix scroll restoration has completed
            requestAnimationFrame(() => {
                setTimeout(() => {
                    const element = document.getElementById(elementId);
                    if (element) {
                        element.scrollIntoView();
                    }
                }, 500); // Increased delay to ensure Remix has finished
            });
        }
    }, [location.hash, professional]);

    return !desktop ? <ContactPageMobile /> : <ContactPageDesktop />;
};

export const handle: APHandle = {
    disableTopNavAnimation: true,
    disableMobileUserScaling: true,
    hideContactArchiPro: true,
};

export default ContactPage;
